// ============================================================================
// components/remote/useRemoteControl.ts — УПРАВЛЕНИЕ сеансом (Ф2):
// host принимает data-сообщения и инжектит ввод (SendInput через Wails-мост);
// viewer захватывает мышь/клавиатуру и шлёт события по data-каналу.
// Контракт: plans/remote-protocol-contract.md §4. Управление по умолчанию
// ВЫКЛЮЧЕНО (показ экрана ≠ разрешение управления): host подтверждает вручную.
//
// Захват ввода вынесен в ЕДИНЫЙ модуль lib/remote/inputCapture.ts (SRP/SSoT):
// тот же код использует контрактный вьюер (useContractViewer) — здесь лишь
// транспорт (publishData LiveKit). Карта клавиш — lib/remote/inputKeys.ts.
// ============================================================================

import { useCallback, useEffect, useRef, useState, type MutableRefObject, type RefObject } from 'react';
import { Room, RoomEvent } from 'livekit-client';
import { logEvent } from '@/lib/eventLog';
import { decodeRemoteData, encodeRemoteData } from '@/lib/remote/remoteSession';
import { startInputCapture, type InputCaptureState } from '@/lib/remote/inputCapture';

interface Options {
  roomRef: MutableRefObject<Room | null>;
  role: 'viewer' | 'host';
  sessionId: string;
  stageRef: RefObject<HTMLDivElement | null>;
  active: boolean;
}

export function useRemoteControl({ roomRef, role, sessionId, stageRef, active }: Options) {
  const [controlOn, setControlOn] = useState(false);
  const [controlRequested, setControlRequested] = useState(false);
  const [inputPackets, setInputPackets] = useState(0);
  const [pointerLocked, setPointerLocked] = useState(false);
  const [keyboardLocked, setKeyboardLocked] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const controlOnRef = useRef(false);
  const errLoggedRef = useRef(false);
  useEffect(() => {
    controlOnRef.current = controlOn;
  }, [controlOn]);

  // Полноэкранный режим (нужен Keyboard Lock: системные клавиши ловятся только
  // в fullscreen — ограничение браузера). Управляет stage-элементом.
  useEffect(() => {
    const onFs = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFs);
    onFs();
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  const toggleFullscreen = useCallback(() => {
    const el = stageRef.current;
    if (!el) return;
    try {
      if (document.fullscreenElement) {
        void document.exitFullscreen?.();
      } else {
        void el.requestFullscreen?.();
      }
    } catch {
      /* API недоступен — работаем без fullscreen */
    }
  }, [stageRef]);

  const log = useCallback((level: 'info' | 'error', message: string, details?: string) => {
    void logEvent({
      level,
      category: 'ui',
      message,
      entityType: 'remote',
      entityCode: 'session',
      objectId: sessionId,
      details: details || '',
    });
  }, [sessionId]);

  const publish = useCallback((obj: unknown) => {
    const room = roomRef.current;
    if (!room) return;
    room.localParticipant
      .publishData(encodeRemoteData(obj), { reliable: true })
      .catch((e: unknown) => log('error', 'удалённый доступ: не удалось отправить данные управления', String((e as Error)?.message || e)));
  }, [roomRef, log]);

  // HOST: приём данных управления
  useEffect(() => {
    if (!active || role !== 'host') return;
    const room = roomRef.current;
    if (!room) return;
    const onData = (payload: Uint8Array) => {
      const msg = decodeRemoteData(payload);
      if (!msg) return;
      if (msg.t === 'control_request') {
        setControlRequested(true);
        log('info', 'удалённый доступ: запрошено управление (ожидает решения)');
        return;
      }
      if (msg.t !== 'input' || !controlOnRef.current) return;
      const events = msg.events as unknown[] | undefined;
      if (!Array.isArray(events) || events.length === 0) return;
      const bridge = window.go?.main?.App?.SendRemoteInput;
      if (!bridge) {
        if (!errLoggedRef.current) {
          errLoggedRef.current = true;
          log('error', 'удалённый доступ: инъекция недоступна (host — только desktop)');
        }
        return;
      }
      bridge(JSON.stringify(events))
        .then((r) => {
          if (r?.ok) {
            setInputPackets((c) => c + 1);
          } else if (!errLoggedRef.current) {
            errLoggedRef.current = true;
            log('error', 'удалённый доступ: инъекция отклонена', r?.error || '');
          }
        })
        .catch((e: unknown) => {
          if (!errLoggedRef.current) {
            errLoggedRef.current = true;
            log('error', 'удалённый доступ: ошибка инъекции', String((e as Error)?.message || e));
          }
        });
    };
    room.on(RoomEvent.DataReceived, onData);
    return () => {
      room.off(RoomEvent.DataReceived, onData);
    };
  }, [active, role, roomRef, log]);

  // VIEWER: состояние управления от хоста
  useEffect(() => {
    if (!active || role !== 'viewer') return;
    const room = roomRef.current;
    if (!room) return;
    const onData = (payload: Uint8Array) => {
      const msg = decodeRemoteData(payload);
      if (msg?.t === 'control') setControlOn(!!msg.on);
    };
    room.on(RoomEvent.DataReceived, onData);
    return () => {
      room.off(RoomEvent.DataReceived, onData);
    };
  }, [active, role, roomRef]);

  // VIEWER: захват ввода (только при включённом управлении) — единый модуль.
  useEffect(() => {
    if (role !== 'viewer' || !controlOn) return;
    const el = stageRef.current;
    if (!el) return;
    const handle = startInputCapture({
      element: el,
      send: (ev) => publish({ t: 'input', events: [ev] }),
      onState: (s: InputCaptureState) => {
        setPointerLocked(s.pointerLocked);
        setKeyboardLocked(s.keyboardLocked);
        setFullscreen(s.fullscreen);
      },
    });
    return () => handle.stop();
  }, [role, controlOn, stageRef, publish]);

  const requestControl = useCallback(() => {
    publish({ t: 'control_request' });
    log('info', 'удалённый доступ: запрошено управление');
  }, [publish, log]);

  const allowControl = useCallback(() => {
    setControlRequested(false);
    setControlOn(true);
    publish({ t: 'control', on: true });
    log('info', 'удалённый доступ: управление разрешено (host)');
  }, [publish, log]);

  const denyControl = useCallback(() => {
    setControlRequested(false);
    publish({ t: 'control', on: false });
    log('info', 'удалённый доступ: управление запрещено (host)');
  }, [publish, log]);

  const stopControl = useCallback(() => {
    setControlOn(false);
    publish({ t: 'control', on: false });
    log('info', 'удалённый доступ: управление остановлено (host)');
  }, [publish, log]);

  return { controlOn, controlRequested, inputPackets, pointerLocked, keyboardLocked, fullscreen, toggleFullscreen, requestControl, allowControl, denyControl, stopControl };
}

export type RemoteControlApi = ReturnType<typeof useRemoteControl>;
